import 'react';
import {ReactElement, ReactNode} from 'react';
import {
    List,
    MenuItem,
    ListItemIcon,
    Typography,
    Collapse,
    Tooltip,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {useSidebarState} from 'react-admin';

interface Props {
    dense: boolean;
    handleToggle: () => void;
    icon: ReactElement;
    isOpen: boolean;
    name: string;
    children: ReactNode;
    /** 0 = children are level-2; 1 = level-3; ... */
    depth?: number;
}

const SubMenu = (props: Props) => {
    const {handleToggle, isOpen, name, icon, children, dense, depth = 0} = props;
    const [sidebarIsOpen] = useSidebarState();

    const header = (
        <MenuItem
            dense={dense}
            onClick={handleToggle}
            sx={{
                color: theme => theme.palette.text.secondary,
                transition: 'padding-left 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
                '& .MuiListItemIcon-root': {
                    minWidth: theme =>
                        sidebarIsOpen ? theme.spacing(5) : theme.spacing(2),
                    transition: 'min-width 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
                    color: 'inherit',
                },
            }}
        >
            <ListItemIcon>
                {isOpen ? <ExpandMore/> : icon}
            </ListItemIcon>
            <Typography
                variant="inherit"
                color="inherit"
                sx={{
                    opacity: sidebarIsOpen ? 1 : 0,
                    width: sidebarIsOpen ? 'auto' : 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, ' +
                        'width 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
                }}
            >
                {name}
            </Typography>
        </MenuItem>
    );

    return (
        <div>
            <Tooltip
                title={name}
                placement="right"
                disableHoverListener={sidebarIsOpen || isOpen}
                disableFocusListener={sidebarIsOpen || isOpen}
                disableTouchListener={sidebarIsOpen || isOpen}
            >
                {header}
            </Tooltip>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List
                    dense={dense}
                    component="div"
                    disablePadding
                    className={`SubMenu SubMenu-depth-${depth}`}
                    sx={{
                        // Only indent this level's items (links + nested SubMenu headers),
                        // not deeper descendants — so level-3 can step further in.
                        '& > .MuiMenuItem-root, & > div > .MuiMenuItem-root': {
                            transition:
                                'padding-left 195ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
                            paddingLeft: theme =>
                                sidebarIsOpen
                                    ? theme.spacing(4 + depth * 2)
                                    : theme.spacing(2),
                        },
                    }}
                >
                    {children}
                </List>
            </Collapse>
        </div>
    );
};

export default SubMenu;
